/**
 * IA Usage dashboard — API client (OpenSpec #2.8).
 *
 * Reusa o axios instance configurado em apiService (baseURL + token interceptor).
 */
import axios from 'axios';
import { apiService } from './api';

export type IaUsageAgent =
  | 'orchestrator'
  | 'field-extractor'
  | 'crm-manager'
  | 'sdr-conversation'
  | 'handoff-classifier'
  | 'guardrails'
  | 'person-detector'
  | 'reactivation-classifier'
  | 'price-intent-classifier'
  | 'other';

export type ModelTier = 'fast' | 'standard' | 'quality';

export interface AiUsageEvent {
  organizationId: string;
  timestamp: string;
  callId: string;
  agent: IaUsageAgent;
  tier?: ModelTier;
  provider: string;
  model: string;
  leadId?: string;
  inputTokens: number;
  outputTokens: number;
  latencyMs: number;
  costProviderUsd: number;
  costBilledUsd: number;
  markupPercentApplied: number;
  markupFlatCentsApplied: number;
  priceUnknown?: boolean;
}

export interface UsageStatsByAgent {
  agent: IaUsageAgent;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costBilledUsd: number;
}

export interface UsageStatsByDay {
  date: string;
  calls: number;
  costBilledUsd: number;
}

export interface UsageStats {
  totalCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostProviderUsd: number;
  totalCostBilledUsd: number;
  byAgent: UsageStatsByAgent[];
  byDay: UsageStatsByDay[];
  topCalls: AiUsageEvent[];
}

export interface UsageQueryResponse {
  from: string;
  to: string;
  filters: { agent?: IaUsageAgent; tier?: ModelTier };
  stats: UsageStats;
}

export interface GetUsageOptions {
  from?: string;
  to?: string;
  agent?: IaUsageAgent;
  tier?: ModelTier;
  organizationId?: string; // super-admin drill-down
}

interface AxiosLike {
  get<T>(url: string, config?: { responseType?: 'blob'; params?: Record<string, string> }): Promise<{ data: T }>;
}

function getClient(): AxiosLike {
  const anySvc = apiService as unknown as { api: AxiosLike };
  return anySvc.api;
}

function buildParams(opts: GetUsageOptions): Record<string, string> {
  const out: Record<string, string> = {};
  if (opts.from) out.from = opts.from;
  if (opts.to) out.to = opts.to;
  if (opts.agent) out.agent = opts.agent;
  if (opts.tier) out.tier = opts.tier;
  if (opts.organizationId) out.organizationId = opts.organizationId;
  return out;
}

export async function getUsageStats(opts: GetUsageOptions = {}): Promise<UsageQueryResponse> {
  try {
    const res = await getClient().get<UsageQueryResponse>('/ia/usage', {
      params: buildParams(opts),
    });
    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      throw new Error(err.response?.data?.message || 'Erro ao carregar uso de IA');
    }
    throw err;
  }
}

export async function exportUsageCsv(opts: GetUsageOptions = {}): Promise<Blob> {
  const res = await getClient().get<Blob>('/ia/usage/export.csv', {
    responseType: 'blob',
    params: buildParams(opts),
  });
  return res.data;
}
